import React, { useEffect, useMemo, useState } from 'react';
import { Layers, Plus, Sparkles, X } from 'lucide-react';
import { Button, Modal, Spinner } from '../Exploration/ui';
import { programmesApi } from './api';

// Pastille du programme dans l'en-tête d'un projet : le programme et son nombre de projets (ouvre l'onglet), ou,
// sans programme, de quoi en créer un (proposition de Nexus, projets proches, programme existant).

const ProjectCheck = ({ project, checked, onChange, current }) => (
  <label className={`flex items-center gap-2 rounded-lg px-2 py-1 text-sm ${current ? 'text-slate-400' : 'text-slate-700 hover:bg-slate-50'}`}>
    <input type="checkbox" checked={checked} disabled={current} onChange={(e) => onChange(e.target.checked)} />
    <span className="min-w-0 flex-1 truncate">{project.name}{project.number ? <span className="ml-1 text-xs text-slate-400">#{project.number}</span> : null}</span>
    <span className="shrink-0 text-[11px] text-slate-400">{project.client_name || ''}{project.start ? ` · ${String(project.start).slice(0, 4)}` : ''}</span>
    {project.programme_id && !current && <span className="shrink-0 rounded bg-amber-50 px-1 text-[10px] font-semibold text-amber-700" title="Il quittera son programme actuel">déjà rangé</span>}
  </label>
);

export function Suggestion({ suggestion, projectId, onDone }) {
  const [name, setName] = useState(suggestion.name);
  const [ids, setIds] = useState(new Set(suggestion.project_ids));
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    try { await programmesApi.acceptSuggestion(suggestion.id, { name, project_ids: [...ids] }); onDone(true); } catch (err) { onDone(false, err.message); }
    setBusy(false);
  };
  const extending = suggestion.programme; // une partie des projets est déjà réunie : on complète ce programme
  return (
    <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700"><Sparkles size={13} /> Proposé par Nexus</p>
      {extending ? (
        <p className="mt-2 text-sm font-semibold text-slate-800">Compléter le programme « {extending.name} »</p>
      ) : (
        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm font-semibold outline-none focus:border-indigo-300" />
      )}
      {suggestion.reason && <p className="mt-1 text-xs text-slate-500">{suggestion.reason}</p>}
      <div className="mt-2 space-y-0.5">
        {suggestion.projects.map((p) => (
          <ProjectCheck key={p.id} project={{ ...p, programme_id: extending && !(suggestion.adding || []).includes(p.id) ? null : p.programme_id }}
            current={p.id === projectId} checked={ids.has(p.id)}
            onChange={(on) => setIds((s) => { const n = new Set(s); if (on) n.add(p.id); else n.delete(p.id); return n; })} />
        ))}
      </div>
      <div className="mt-2 flex justify-end gap-1.5">
        <Button size="xs" variant="ghost" icon={X} disabled={busy} onClick={async () => { await programmesApi.rejectSuggestion(suggestion.id); onDone(false); }}>Ignorer</Button>
        <Button size="xs" variant="indigo" disabled={busy || ids.size < 2 || !name.trim()} onClick={accept}>{busy ? <Spinner /> : extending ? 'Ajouter au programme' : 'Créer ce programme'}</Button>
      </div>
    </div>
  );
}

function Picker({ projectId, projectName, data, onClose, onChanged }) {
  const [programmes, setProgrammes] = useState(null);
  const [name, setName] = useState(() => (projectName || '').replace(/\b(20\d\d|2k\d\d|v\d+)\b/gi, '').replace(/\s+/g, ' ').trim());
  const [ids, setIds] = useState(new Set());
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [asked, setAsked] = useState(false);
  useEffect(() => { programmesApi.list().then((r) => setProgrammes(r.programmes)).catch(() => setProgrammes([])); }, []);
  const similar = data?.similar || [];
  const done = (ok, message) => { if (message) setError(message); else if (ok) onChanged(); else onChanged(false); };
  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      await programmesApi.create({ name, project_ids: [projectId, ...ids] });
      onChanged();
    } catch (err) { setError(err.message); }
    setBusy(false);
  };
  const join = async () => {
    setBusy(true);
    try { await programmesApi.addProject(Number(target), projectId); onChanged(); } catch (err) { setError(err.message); }
    setBusy(false);
  };
  const others = useMemo(() => (programmes || []).filter((p) => !p.projects.some((x) => x.id === projectId)), [programmes, projectId]);
  return (
    <Modal open onClose={onClose} title="Ranger ce projet dans un programme" width="max-w-xl"
      footer={<Button variant="ghost" onClick={onClose}>Fermer</Button>}>
      <p className="mb-3 text-sm text-slate-600">
        Un programme réunit les projets qui sont les étapes d’un même effort (le même produit ou le même problème, repris d’année en année).
        Chaque projet garde ses outils ; l’onglet Programme montre la frise, le C/K et la carte de tous, et Nexus s’en sert.
      </p>
      {error && <p className="mb-2 rounded-lg bg-rose-50 px-2 py-1 text-xs text-rose-700">{error}</p>}
      <div className="space-y-3">
        {(data?.suggestions || []).map((s) => <Suggestion key={s.id} suggestion={s} projectId={projectId} onDone={done} />)}
        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Nouveau programme</p>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom (Vérin, Capteur hydrostatique…)" className="mt-2 w-full rounded-lg border border-slate-200 px-2 py-1 text-sm outline-none focus:border-indigo-300" />
          {similar.length > 0 && (
            <div className="mt-2">
              <p className="mb-1 text-xs text-slate-500">Projets proches à y ranger aussi :</p>
              <div className="max-h-56 space-y-0.5 overflow-y-auto">
                {similar.map((p) => (
                  <ProjectCheck key={p.id} project={p} checked={ids.has(p.id)}
                    onChange={(on) => setIds((s) => { const n = new Set(s); if (on) n.add(p.id); else n.delete(p.id); return n; })} />
                ))}
              </div>
            </div>
          )}
          <div className="mt-2 flex justify-end">
            <Button size="xs" variant="indigo" icon={busy ? undefined : Plus} disabled={busy || !name.trim()} onClick={create}>{busy ? <Spinner /> : 'Créer'}</Button>
          </div>
        </div>
        {others.length > 0 && (
          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Programme existant</p>
            <div className="mt-2 flex gap-2">
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
                <option value="">Choisir…</option>
                {others.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.projects.length} projets)</option>)}
              </select>
              <Button size="xs" disabled={!target || busy} onClick={join}>Ajouter</Button>
            </div>
          </div>
        )}
        <button type="button" disabled={asked} onClick={async () => { setAsked(true); await programmesApi.runSuggestions().catch(() => {}); }}
          className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 disabled:text-slate-400">
          <Sparkles size={12} /> {asked ? 'Nexus cherche des regroupements : ses propositions arriveront ici et sur la liste des projets.' : 'Demander à Nexus de chercher des regroupements dans tous les projets'}
        </button>
      </div>
    </Modal>
  );
}

export default function ProgrammeChip({ projectId, projectName, state, openTab }) {
  const [open, setOpen] = useState(false);
  const data = state?.data;
  if (!data?.access) return null;
  const programme = data.programme;
  if (programme) {
    return (
      <button type="button" onClick={() => openTab('programme')} title={`Programme ${programme.name} : ${programme.projects.map((p) => p.name).join(', ')}`}
        className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-inset ring-violet-200 hover:bg-violet-100">
        <Layers size={12} /> {programme.name} · {programme.projects.length}
      </button>
    );
  }
  const suggested = data.suggestions?.length > 0;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${suggested
          ? 'bg-indigo-50 text-indigo-700 ring-indigo-200 hover:bg-indigo-100' : 'text-slate-400 ring-slate-200 hover:bg-white hover:text-slate-600'}`}>
        {suggested ? <><Sparkles size={12} /> Programme {data.suggestions[0].name} ?</> : <><Layers size={12} /> Programme</>}
      </button>
      {open && <Picker projectId={projectId} projectName={projectName} data={data} onClose={() => setOpen(false)}
        onChanged={async (ok = true) => { const next = await state.reload(); if (ok !== false && next?.programme) { setOpen(false); openTab('programme'); } }} />}
    </>
  );
}
